'use client'

// import { MatterHistoryFolderSelect } from '@repo/ui/MatterHistoryFolderSelect'
import { MatterHistoryTypeSelect } from '@repo/ui/MatterHistoryTypeSelect'
import { RetentionPolicySelect } from '@repo/ui/RetentionPolicySelect'
import { useEffect, useState } from 'react'
import { useMatterHistoryStore } from '@repo/store/useMatterHistoryStore'
import MUIButton from '@mui/material/Button'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'

const CONFIGURABLE_FIELDS = [
  'matterHistoryDetailForSentEmail',
  'matterHistoryDetailForReceivedEmail',
  'matterHistoryFolderId',
  'matterHistoryTypeId',
  'retentionPolicyId',
  'retentionDateType',
  'isSaveEmailBody',
  'isSaveEmailAttachments',
  'isSaveEmailAttachmentsSeparately',
  'isIgnoreInlineImages',
  'ignoreInlineImagesBelowKilobytes',
  'deleteDuplicateImagesAboveCount'
] as const

export const MatterHistorySettingsDefaults = () => {
  const {
    createMatterHistoryEmailConfiguration,
    updateMatterHistoryEmailConfiguration,
    fetchMatterHistoryPracticeDefaults,
    fetchMatterHistorySettingsDefaults
  } = useMatterHistoryStore()
  const [formValues, setFormValues] = useState<Record<string, any>>({})
  const [ospreyDefaults, setOspreyDefaults] = useState<Record<string, any>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [defaults, practiceDefaults] = await Promise.all([
          fetchMatterHistorySettingsDefaults(),
          fetchMatterHistoryPracticeDefaults({ resolveParent: true })
        ])

        setOspreyDefaults(defaults)
        setFormValues(practiceDefaults)
      } catch (error) {
        console.error('Error fetching matter history defaults:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const updateFormValue = (updates: Record<string, any>) => {
    setFormValues((prev) => ({ ...prev, ...updates }))
  }

  const handleSave = async () => {
    setIsSaving(true)

    try {
      const payload: Record<string, any> = {
        matterHistoryEmailConfigurationId: formValues.matterHistoryEmailConfigurationId,
        emailConversationId: null
      }

      for (const key of CONFIGURABLE_FIELDS) {
        payload[key] = formValues[key] === ospreyDefaults[key] ? null : formValues[key]
      }

      const isCreate = formValues.matterHistoryEmailConfigurationId === 0

      const result = isCreate
        ? await createMatterHistoryEmailConfiguration(payload)
        : await updateMatterHistoryEmailConfiguration(payload)

      if (isCreate && result?.matterHistoryEmailConfigurationId) {
        setFormValues((prev) => ({
          ...prev,
          matterHistoryEmailConfigurationId: result.matterHistoryEmailConfigurationId
        }))
      }
    } catch (error) {
      console.error('Error saving matter history defaults:', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <MUIStack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <MUICircularProgress />
      </MUIStack>
    )
  }

  return (
    <MUIStack data-testid="MatterHistorySettingsDefaults" direction={'column'} gap={4}>
      <MUITypography variant="body2" sx={{ mt: -1, mb: 5 }}>
        <em>
          Configure practice-level default values for Matter History Settings. These defaults are used as the starting
          values when users compose emails.
        </em>
      </MUITypography>

      <MUITextField
        label="Details - outbound messages"
        variant="outlined"
        type="text"
        value={formValues?.matterHistoryDetailForSentEmail ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          updateFormValue({ matterHistoryDetailForSentEmail: e.target.value })
        }
      />

      <MUITextField
        label="Details - inbound messages"
        variant="outlined"
        type="text"
        value={formValues?.matterHistoryDetailForReceivedEmail ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          updateFormValue({ matterHistoryDetailForReceivedEmail: e.target.value })
        }
      />

      {/* <MatterHistoryFolderSelect
        value={formValues?.matterHistoryFolderId ?? null}
        onChange={(matterHistoryFolderId: any) =>
          updateFormValue({ matterHistoryFolderId: matterHistoryFolderId })
        }
        workTypeId={undefined}
      /> */}
      <MatterHistoryTypeSelect
        value={formValues?.matterHistoryTypeId ?? null}
        onChange={(id) => updateFormValue({ matterHistoryTypeId: id })}
      />
      <RetentionPolicySelect
        value={formValues?.retentionPolicyId ?? null}
        onChange={(value: any) => updateFormValue({ retentionPolicyId: value })}
      />
      {formValues?.retentionPolicyId && (
        <MUIRadioGroup
          name="RetentionDate"
          value={
            formValues?.retentionDateType === 1
              ? 'Archive date'
              : formValues?.retentionDateType === 2
                ? `Today's date`
                : null
          }
          onChange={(e) =>
            e.target.value === 'Archive date'
              ? updateFormValue({ retentionDateType: 1 })
              : e.target.value === `Today's date`
                ? updateFormValue({ retentionDateType: 2 })
                : null
          }
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            mt: -2,
            mb: 0
          }}
        >
          <MUITypography variant="body1" sx={{ alignSelf: 'center', mr: 4, mt: 4, mb: 4, textTransform: 'uppercase' }}>
            Base the retention date on:
          </MUITypography>
          <MUIFormControlLabel value={'Archive date'} control={<MUIRadio />} label="Archive date" sx={{ mr: 10 }} />
          <MUIFormControlLabel value={`Today's date`} control={<MUIRadio />} label="Today's date" />
        </MUIRadioGroup>
      )}
      <MUIStack direction="column" gap={1} mt={2} mb={6}>
        <MUITypography variant="body1">INCLUDE:</MUITypography>
        <MUIStack direction="row" gap={2}>
          <MUIRadioGroup
            name="IncludeTypeForSentEmail"
            value={
              formValues?.isSaveEmailBody && formValues?.isSaveEmailAttachments
                ? 'BodyAndAttachments'
                : formValues?.isSaveEmailBody
                  ? 'BodyOnly'
                  : formValues?.isSaveEmailAttachments
                    ? 'AttachmentsOnly'
                    : null
            }
            onChange={(e) =>
              e.target.value === 'BodyAndAttachments'
                ? updateFormValue({ isSaveEmailBody: true, isSaveEmailAttachments: true })
                : e.target.value === `BodyOnly`
                  ? updateFormValue({ isSaveEmailBody: true, isSaveEmailAttachments: false })
                  : e.target.value === `AttachmentsOnly`
                    ? updateFormValue({ isSaveEmailBody: false, isSaveEmailAttachments: true })
                    : null
            }
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              mt: -1,
              mb: 0
            }}
          >
            <MUIFormControlLabel
              value={'BodyAndAttachments'}
              control={<MUIRadio />}
              label="Message body and attachments"
            />
            <MUIFormControlLabel value={`BodyOnly`} control={<MUIRadio />} label="Message body only" />
            <MUIFormControlLabel value={`AttachmentsOnly`} control={<MUIRadio />} label="Attachments only" />
          </MUIRadioGroup>
        </MUIStack>
      </MUIStack>

      <MUIButton
        variant="contained"
        color="primary"
        size="large"
        onClick={handleSave}
        disabled={isSaving}
        sx={{ maxWidth: 160, width: '100%', margin: '0 0 0 auto' }}
      >
        {isSaving ? <MUICircularProgress size={24} color="inherit" /> : 'Save'}
      </MUIButton>
    </MUIStack>
  )
}
